import { App, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"

import Button from "../../../../../../../Components/Button"
import Icon from "../../../../../../../Icon"
import {
  emptyEnrichmentRow,
  ENRICHMENT_ARRAY_SECTIONS,
  ENRICHMENT_SECTIONS
} from "../../../schema/enrichment"
import EnrichmentField from "../../EnrichmentField/EnrichmentField"

import groupStyles from "../../group.module.less"
import paneStyles from "../enrichmentPane.module.less"
import styles from "./enrichmentDataFields.module.less"

const { Text } = Typography

// Structured per-dispute facts (verification results, shipping proof, customer
// history). Single-value sections first, then the repeatable ones: those grow, so
// keeping them below the fixed-length sections keeps the form's top half stable.
const EnrichmentDataFields = ({ values, onChange, isActionable }) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()

  // Repeatable sections (shipping, order items, usage logs, refunds) store an array of
  // row objects under the section key. Rows always exist as a whole object, so editing
  // one cell never has to create the row first.
  const rowsOf = (section) => (Array.isArray(values[section.key]) ? values[section.key] : [])

  const handleRowChange = (section, index, fieldKey, value) =>
    onChange(
      section.key,
      rowsOf(section).map((row, i) => (i === index ? { ...row, [fieldKey]: value } : row))
    )

  const handleAddRow = (section) =>
    onChange(section.key, [...rowsOf(section), emptyEnrichmentRow(section)])

  // Removing a repeatable entry drops whatever was typed into it, so confirm first
  // (same modal pattern as Approve/Reopen). Only removes on confirm.
  const confirmRemoveRow = (section, index) =>
    modal.confirm({
      title: t("Remove this entry?"),
      icon: null,
      content: <Text type="secondary">{t("This information will be lost.")}</Text>,
      okText: t("Remove"),
      okButtonProps: { danger: true },
      cancelText: t("Cancel"),
      centered: true,
      onOk: () =>
        onChange(
          section.key,
          rowsOf(section).filter((_, i) => i !== index)
        )
    })

  return (
    <>
      <Text type="secondary" className={paneStyles.intro}>
        {isActionable
          ? t(
              "Fill in what you know. Every field here can strengthen the evidence PDF, so use Save & Update PDF below once you are done to see your changes in the document."
            )
          : t("These fields are read-only: this dispute is no longer actionable.")}
      </Text>
      {ENRICHMENT_SECTIONS.map((section) => (
        <div className={cls(groupStyles.groupCard, groupStyles.groupCardStacked)} key={section.key}>
          <div className={groupStyles.groupLabel}>{t(section.label)}</div>
          <div className={styles.grid}>
            {section.fields.map((field) => (
              <EnrichmentField
                key={field.key}
                field={field}
                value={values[field.key]}
                disabled={!isActionable}
                onChange={(v) => onChange(field.key, v)}
              />
            ))}
          </div>
        </div>
      ))}
      {ENRICHMENT_ARRAY_SECTIONS.map((section) => {
        const rows = rowsOf(section)
        return (
          <div
            className={cls(groupStyles.groupCard, groupStyles.groupCardStacked)}
            key={section.key}
          >
            <div className={groupStyles.groupLabel}>
              {t(section.label)}
              <Text type="secondary" className={styles.rowsCount}>
                {rows.length === 1 ? t("1 entry") : t("_n_ entries", { n: rows.length })}
              </Text>
            </div>
            {rows.map((row, index) => (
              <div className={styles.row} key={index}>
                <div className={styles.grid}>
                  {section.fields.map((field) => (
                    <EnrichmentField
                      key={field.key}
                      field={field}
                      value={row[field.key]}
                      disabled={!isActionable}
                      onChange={(v) => handleRowChange(section, index, field.key, v)}
                    />
                  ))}
                </div>
                {isActionable ? (
                  <Button
                    type="text"
                    danger
                    className={cls("compact-btn", styles.removeRow)}
                    icon={<Icon type="delete" />}
                    onClick={() => confirmRemoveRow(section, index)}
                    text={t("Remove")}
                  />
                ) : null}
              </div>
            ))}
            {isActionable ? (
              <Button
                type="text"
                className={cls("compact-btn", styles.addRow)}
                icon={<Icon type="plus" />}
                onClick={() => handleAddRow(section)}
                text={t(section.addLabel)}
              />
            ) : null}
            {!rows.length && !isActionable ? (
              <Text type="secondary">{t("None recorded.")}</Text>
            ) : null}
          </div>
        )
      })}
    </>
  )
}

export default EnrichmentDataFields
